<script lang="ts">
	/** Product families — the unit §9.3's rank rule and §9.2's one-trial rule are defined over. */

	import { Badge, Button, Checkbox, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell, Textarea, Toggle } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import {
		ConfirmButton,
		DragHandle,
		DragSort,
		EmptyState,
		FormField,
		FormModal,
		ImageField,
		Panel,
		PostPurchaseEditor
	} from '../../../components';
	import { archiveFamily, createFamily, reorderFamilies, updateFamily } from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import { mediaURL, moveKeyBefore, moveKeyBy, postPurchaseDraftOf, postPurchaseInput } from '../../../Utilities';

	/**
	 * The terms scoped to this line (§4.7).
	 *
	 * Resolved from the terms list rather than from `family.clickwrapUUIDs`, so the two panels on
	 * this page answer the question the same way and cannot drift about it. A term named here
	 * covers **every** product in the line — including ones added next month, which is the whole
	 * reason a family scope exists and the reason it earns a column: this is the one place a term
	 * reaches products that never name it themselves.
	 */
	function fnTermsFor(family: ProductFamily): AdminClickwrap[] {
		return clickwraps.filter(term => term.familySlugs.includes(family.slug));
	}
	import { isArchivedFamily } from '../../../Utilities';
	import type { AdminClickwrap, AdminProductOption, ProductFamily } from '../../../AppTypes';

	export let families: ProductFamily[] = [];
	/**
	 * The terms that exist, so a family can be ticked into the ones covering its whole line (§4.7).
	 *
	 * Ticking one attaches **this family** and nothing else: a term covering three lines keeps the
	 * other two.
	 */
	export let clickwraps: AdminClickwrap[] = [];
	/**
	 * The options that exist, so a line can be ticked into the ones offered across it (§4.8).
	 *
	 * Ticking one attaches **this family** and nothing else, exactly as a term does — and offering
	 * one on a line offers it on every product in it, including ones added next month.
	 */
	export let productOptions: AdminProductOption[] = [];
	export let loading: boolean = false;
	/**
	 * Whether archived lines are listed (§4.1). Owned by the catalogue page, because the same
	 * switch governs the products panel — "archived" is one idea and two switches would disagree.
	 */
	export let showArchived: boolean = false;

	/**
	 * A family has no draft state — it is created active and only archiving deactivates it — so
	 * inactive *is* archived here. `isArchivedFamily` says so in one place, shared with the
	 * catalogue page's count and with the products panel's group headings.
	 */
	$: visible = showArchived ? families : families.filter(family => !isArchivedFamily(family));
	$: hiddenCount = families.length - visible.length;

	const dispatcher = createEventDispatcher();

	let isAdding: boolean = false;
	let saving: boolean = false;
	/** True while an order is being saved: dragging during the round trip drops onto a stale list. */
	let ordering: boolean = false;

	/**
	 * The order the lines are shown in (§14) — presentation, and deliberately not `Product.rank`.
	 *
	 * Applied locally first so the row lands where it was dropped, then sent as the **whole** order
	 * rather than a moved item and a position: two admins dragging at once would otherwise apply
	 * "move Atelier to position 2" against a list that has since changed. The server answers with
	 * the order it now holds, and a refusal re-reads the panel — so the screen never keeps an
	 * arrangement the store did not accept.
	 */
	async function fnApplyOrder(slugs: string[]): Promise<void> {
		if (ordering) return;

		const previous = families;
		families = slugs
			.map(slug => families.find(family => family.slug === slug))
			.filter((family): family is ProductFamily => family !== undefined);

		ordering = true;
		try {
			await reorderFamilies(slugs);
			dispatcher('changed');
		} catch (error) {
			families = previous;
			toastError('The order was not saved.', (error as Error).message);
		} finally {
			ordering = false;
		}
	}

	function fnDropped(key: string, targetKey: string): void {
		const slugs = families.map(family => family.slug);
		void fnApplyOrder(moveKeyBefore(slugs, key, targetKey));
	}

	function fnMoved(slug: string, direction: number): void {
		const slugs = families.map(family => family.slug);
		const next = moveKeyBy(slugs, slug, direction);
		// Nothing moved — the top row pressed up, or the bottom pressed down. Sending it would be a
		// write with no change in it, and an audit entry saying the catalogue was rearranged.
		if (next.every((value, index) => value === slugs[index])) return;
		void fnApplyOrder(next);
	}
	let busySlug: string = '';
	let draft = { slug: '', name: '', description: '', imageUUID: null as string | null, imageURL: null as string | null };

	/** The family being edited, addressed by slug — families have no uuid (§4.1). */
	let editingSlug: string = '';
	let edit = {
		name: '',
		description: '',
		clickwrapUUIDs: [] as string[],
		optionUUIDs: [] as string[],
		imageUUID: null as string | null,
		imageURL: null as string | null,
		postPurchase: postPurchaseDraftOf(null)
	};
	let postPurchaseValid: boolean = true;

	/** Toggling one term, kept out of the markup so the list stays readable. */
	function fnToggleTerm(list: string[], uuid: string, event: Event): string[] {
		return (event.target as HTMLInputElement).checked
			? [...list, uuid]
			: list.filter(entry => entry !== uuid);
	}

	$: editing = families.find(family => family.slug === editingSlug) ?? null;
	$: canSaveEdit = !saving && edit.name.trim().length > 0 && postPurchaseValid;

	function fnStartEdit(family: ProductFamily): void {
		editingSlug = family.slug;
		edit = {
			name: family.name,
			description: family.description ?? '',
			clickwrapUUIDs: [...(family.clickwrapUUIDs ?? [])],
			optionUUIDs: [...(family.optionUUIDs ?? [])],
			imageUUID: family.imageUUID ?? null,
			imageURL: family.imageURL ?? null,
			postPurchase: postPurchaseDraftOf(family.postPurchase)
		};
	}

	/**
	 * The slug is absent on purpose.
	 *
	 * It is the family's identity — the storefront path `/Products/:familySlug` and the key every
	 * product references. Renaming it would break links customers already hold and any bookmark to
	 * the family page, so the display name is editable and the address is not.
	 */
	async function fnSaveEdit(): Promise<void> {
		if (editing === null) {
			return;
		}
		saving = true;
		try {
			await updateFamily(editing.slug, {
				name: edit.name.trim(),
				description: edit.description.trim().length === 0 ? null : edit.description.trim(),
				clickwrapUUIDs: edit.clickwrapUUIDs,
				optionUUIDs: edit.optionUUIDs,
				// Null is a value — it removes the logo — so it is always sent rather than only
				// when set, which would make removing one impossible to express.
				imageUUID: edit.imageUUID,
				postPurchase: postPurchaseInput(edit.postPurchase)
			});
			toastSuccess(`${edit.name.trim()} saved.`);
			editingSlug = '';
			dispatcher('changed');
		} catch (error) {
			toastError('The family was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	$: slugError =
		draft.slug.length === 0 || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(draft.slug)
			? ''
			: 'Lowercase letters, digits and single hyphens.';
	$: canCreate = !saving && slugError === '' && draft.slug.length > 0 && draft.name.trim().length > 0;

	async function fnCreate(): Promise<void> {
		saving = true;
		try {
			await createFamily({
				slug: draft.slug,
				name: draft.name.trim(),
				description: draft.description.trim().length === 0 ? null : draft.description.trim(),
				imageUUID: draft.imageUUID
			});
			toastSuccess(`${draft.name.trim()} added.`);
			draft = { slug: '', name: '', description: '', imageUUID: null, imageURL: null };
			isAdding = false;
			dispatcher('changed');
		} catch (error) {
			toastError('The family was not added.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Archives a line: the family **and** every product in it, in one server transaction.
	 *
	 * Distinct from the Active toggle, and deliberately a separate control. The toggle moves the
	 * family alone, and a product carries its own `isActive` and stays reachable by direct link
	 * regardless of its line — so "deactivate the family" leaves an admin believing a line is
	 * closed while the storefront still sells every tier in it.
	 */
	async function fnArchive(family: ProductFamily): Promise<void> {
		busySlug = family.slug;
		try {
			await archiveFamily(family.slug);
			toastSuccess(
				`${family.name} archived.`,
				'Its products were deactivated too. Existing subscriptions keep billing, because archiving only stops new purchases.'
			);
			dispatcher('changed');
		} catch (error) {
			toastError('The family was not archived.', (error as Error).message);
		} finally {
			busySlug = '';
		}
	}

	async function fnToggleActive(family: ProductFamily, isActive: boolean): Promise<void> {
		busySlug = family.slug;
		try {
			await updateFamily(family.slug, { isActive });
			toastSuccess(`${family.name} saved.`);
			dispatcher('changed');
		} catch (error) {
			toastError('The family was not changed.', (error as Error).message);
			dispatcher('changed');
		} finally {
			busySlug = '';
		}
	}

	function fnCheckedFrom(event: Event): boolean {
		return (event.target as HTMLInputElement).checked;
	}
</script>

<Panel title="Product families" faIcon="layer-group">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (isAdding = !isAdding)}>
			<i class="fa-solid fa-plus mr-2" />Add family
		</Button>
	</svelte:fragment>

	{#if loading}
		<EmptyState variant="loading" title="Loading families…" />
	{:else if families.length === 0}
		<EmptyState
			title="No product family yet."
			description="A family is what makes upgrade and downgrade definable: tiers inside one family are ranked against each other, and a move across families is a separate purchase."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first family</Button>
		</EmptyState>
	{:else if visible.length === 0}
		<!--
			Every line there is has been archived. Said in those words rather than reusing the empty
			state above: "No product family yet" over a store that has archived four of them is a
			screen inviting an admin to author a line they already have.
		-->
		<EmptyState
			title="Every product family is archived."
			description="Turn on “Show archived” above to see them, or add a new product family."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add a family</Button>
		</EmptyState>
	{:else}
		<!--
			Drag to reorder (§14). The wrapper owns the pointer; the row it reports is turned into a
			whole order here, where the list lives.
		-->
		<DragSort disabled={ordering} on:reorder={event => fnDropped(event.detail.key, event.detail.targetKey)} let:overKey let:draggingKey>
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<!-- The grip's column. Headed blank: it labels an action, not a field. -->
				<TableHeadCell class="w-8" />
				<TableHeadCell>Name</TableHeadCell>
				<TableHeadCell>Products</TableHeadCell>
				<TableHeadCell>Terms</TableHeadCell>
				<TableHeadCell>Active</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each visible as family (family.slug)}
					<TableBodyRow
						data-sort-key={family.slug}
						class={overKey === family.slug
							? 'border-t-2 border-t-primary-500'
							: draggingKey === family.slug
								? 'opacity-50'
								: ''}
					>
						<TableBodyCell class="dataTable !px-2">
							<DragHandle
								label={family.name}
								disabled={ordering}
								on:move={event => fnMoved(family.slug, event.detail.direction)}
							/>
						</TableBodyCell>
						<!--
							Name over slug in one cell, the same block the products table uses.
							
							They were two columns, which made the slug read as a field of its own with
							equal weight to the name — on a screen where the name is what an admin scans
							for and the slug is the URL segment they check. One cell also lets the two
							tables on this page line up: a line and a product are the same kind of thing
							to whoever is reading them, and they were laid out as though they were not.

							The logo sits beside the pair rather than in a column of its own: a line
							without one would otherwise get an empty cell, which reads as missing data.
							Through `mediaURL`, because the server hands out a server-relative path and
							this client is pointed at a separate backend origin.
						-->
						<TableBodyCell class="dataTable">
							<div class="flex items-start gap-2.5">
								{#if family.imageURL}
									<img
										src={mediaURL(family.imageURL)}
										alt=""
										class="w-8 h-8 rounded-lg object-contain shrink-0 mt-0.5"
										loading="lazy"
									/>
								{/if}
								<div class="min-w-0">
									<div class="flex flex-wrap items-center gap-2">
										<span class="font-semibold">{family.name}</span>
										<!--
											Only reachable with "Show archived" on, and said in words beside
											the name rather than left to the dimmed Active toggle at the far
											end of the row: what is being read here is a list an admin has
											deliberately asked to see the retired half of.
										-->
										{#if isArchivedFamily(family)}
											<Badge color="dark">Archived</Badge>
										{/if}
									</div>
									<div class="text-xs text-default font-mono">{family.slug}</div>
								</div>
							</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">{family.products.length}</TableBodyCell>
						<!--
							A term scoped here covers **every** product in the line (§4.7), which is the
							whole reason a family scope exists — and the reason it is worth a column: it
							is the one place a term reaches products that do not name it themselves.
						-->
						<TableBodyCell class="dataTable">
							{@const terms = fnTermsFor(family)}
							{#if terms.length === 0}
								<span class="text-default">—</span>
							{:else}
								<div class="flex flex-wrap gap-1 max-w-[16rem]">
									{#each terms as term (term.uuid)}
										<Badge color="dark" class={term.isActive ? '' : 'opacity-60'}>
											{term.title}{#if !term.isActive}&nbsp;(inactive){/if}
										</Badge>
									{/each}
								</div>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<Toggle
								checked={family.isActive}
								disabled={busySlug === family.slug}
								on:change={event => fnToggleActive(family, fnCheckedFrom(event))}
							/>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex justify-end gap-2">
								<Button size="xs" color="alternative" on:click={() => fnStartEdit(family)}>
									<i class="fa-solid fa-pen mr-1" />Edit
								</Button>
								{#if family.isActive}
									<ConfirmButton
										size="xs"
										color="red"
										disabled={busySlug === family.slug}
										confirmText={`Archive “${family.name}”? Its ${family.products.length} product${family.products.length === 1 ? '' : 's'} are deactivated with it, so nobody new can buy any tier in the family. Existing subscriptions keep billing.`}
										on:confirm={() => fnArchive(family)}
									>
										<i class="fa-solid fa-box-archive mr-1" />Archive
									</ConfirmButton>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
		</DragSort>
	{/if}

	{#if editing !== null}
		<FormModal
			open={true}
			title={`Edit ${editing.name}`}
			faIcon="pen"
			submitLabel="Save family"
			size="md"
			busy={saving}
			canSubmit={canSaveEdit}
			on:submit={fnSaveEdit}
			on:cancel={() => (editingSlug = '')}
		>
			<FormField
				id="family-edit-slug"
				label="Slug"
				description="The family's address, and the key every product references. Fixed once created, because changing it would break the storefront links customers already hold."
			>
				<Input id="family-edit-slug" value={editing.slug} readonly disabled class="font-mono" />
			</FormField>

			<FormField id="family-edit-name" label="Name" required>
				<Input id="family-edit-name" bind:value={edit.name} disabled={saving} />
			</FormField>

			<FormField label="Logo" description="Shown on the storefront beside the family's name.">
				<ImageField bind:uuid={edit.imageUUID} bind:url={edit.imageURL} disabled={saving} label="Family logo" />
			</FormField>

			<FormField id="family-edit-description" label="Description">
				<Textarea id="family-edit-description" bind:value={edit.description} rows={2} disabled={saving} />
			</FormField>

			<FormField
				id="family-post-purchase-message"
				label="After purchase"
				description="Shown the moment a purchase of any product in this family completes — a thank-you and where to go next. A product with its own replaces this one."
			>
				<PostPurchaseEditor id="family-post-purchase" bind:value={edit.postPurchase} bind:valid={postPurchaseValid} disabled={saving} />
			</FormField>

			{#if clickwraps.length > 0}
				<!--
					A family term covers every product in the line. Ticking one here attaches this
					family to it; the term keeps whatever other lines and plans it already covers.
				-->
				<FormField
					label="Purchase terms"
					description="Every buyer of every product in this family must accept these before purchasing."
				>
					<div class="space-y-1">
						{#each clickwraps as term (term.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.clickwrapUUIDs.includes(term.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.clickwrapUUIDs = fnToggleTerm(edit.clickwrapUUIDs, term.uuid, event))}
								/>
								{term.title}
								{#if !term.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}

			{#if productOptions.length > 0}
				<!--
					§4.8. The same shape as the terms above, and one word different in the copy:
					these are offered rather than required, so nothing here blocks a purchase.
				-->
				<FormField
					label="Product options"
					description="Offered to every buyer of every product in this family. Taking one is always their choice."
				>
					<div class="space-y-1">
						{#each productOptions as option (option.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.optionUUIDs.includes(option.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.optionUUIDs = fnToggleTerm(edit.optionUUIDs, option.uuid, event))}
								/>
								{option.title}
								{#if !option.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}
		</FormModal>
	{/if}

	<FormModal
		bind:open={isAdding}
		title="New product family"
		faIcon="plus"
		submitLabel="Add family"
		size="md"
		busy={saving}
		on:submit={fnCreate}
	>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField id="family-slug" label="Slug" required description="The URL segment, e.g. polyverse." error={slugError}>
					<Input id="family-slug" bind:value={draft.slug} placeholder="polyverse" disabled={saving} />
				</FormField>
				<FormField id="family-name" label="Name" required>
					<Input id="family-name" bind:value={draft.name} placeholder="Polyverse" disabled={saving} />
				</FormField>
			</div>
			<FormField label="Logo" description="Shown on the storefront beside the family's name.">
				<ImageField bind:uuid={draft.imageUUID} bind:url={draft.imageURL} disabled={saving} label="Family logo" />
			</FormField>

			<FormField id="family-description" label="Description">
				<Textarea id="family-description" bind:value={draft.description} rows={2} disabled={saving} />
			</FormField>
	</FormModal>
</Panel>
